import { Box, Button, Typography } from "@mui/material";
import Modal from "@mui/material/Modal";
import CrossIcon from "assets/icons/cross.svg";
import { DirectLinkAvailabilitySetup } from "lib/features/availability/types";
import { Item } from "lib/features/item/types";
import { selectCustomerDetails } from "lib/features/message/messageSlice";
import moment from "moment";
import React from "react";
import { useSelector } from "react-redux";
import { availabilityModalStyles } from "styles/AvailabilityDirectory/availabilityModal.styles";
import { formatCustomerName } from "util/formatCustomerName";
import { getItemImageSource } from "util/getImageSource";

interface ShareAvailabilityData {
  item_id: number;
  start_date: Date;
  times: Date;
}

interface ShareDirectLinkModalProps {
  open: boolean;
  close: () => void;
  customStyle?: object;
  sx?: object;
  createAndShareAvailabilityData?: DirectLinkAvailabilitySetup | null;
  shareAvailabilityData?: ShareAvailabilityData;
  item: Item;
  loading: boolean;
  fhPrice?: number;
  processShareBookingLink?: () => void;
  processCreateAndShareBookingLink?: (
    values: DirectLinkAvailabilitySetup
  ) => void;
}

const ShareDirectLinkModal = ({
  open,
  close,
  customStyle,
  sx,
  item,
  createAndShareAvailabilityData,
  shareAvailabilityData,
  loading,
  fhPrice,
  processCreateAndShareBookingLink,
  processShareBookingLink,
}: ShareDirectLinkModalProps) => {
  const customerDetails = useSelector(selectCustomerDetails);

  const availabilityData =
    createAndShareAvailabilityData ||
    (shareAvailabilityData && {
      item_id: shareAvailabilityData.item_id,
      start_date: shareAvailabilityData.start_date,
      times: { start_time: shareAvailabilityData.times },
    });

  const price =
    item.item_type === 4 ? fhPrice ?? 0 : item.itemDetails[0]?.price ?? 0;
  const commission =
    item.item_type === 4
      ? item.commission ?? 0
      : item.itemDetails[0]?.comission ?? 0;

  const commissionAmount = (price * commission) / 100;
  const discountedPrice = price - commissionAmount;

  const itemName =
    item.item_type === 4
      ? item.farehabor_item_details?.name
      : item.itemDetails[0].item_name;
  const startDate = moment.utc(availabilityData?.start_date);
  const startTime = moment.utc(availabilityData?.times.start_time);
  const duration = item.itemDetails[0]?.duration ?? 0;
  const endTime = startTime.add(duration, "minutes");

  const handleShareClick = () => {
    if (createAndShareAvailabilityData && processCreateAndShareBookingLink) {
      processCreateAndShareBookingLink(createAndShareAvailabilityData);
    } else if (shareAvailabilityData && processShareBookingLink) {
      console.log("Sharing booking link...");
      processShareBookingLink();
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      aria-labelledby="customer-modal-title"
      aria-describedby="customer-modal-description"
      sx={{ ...sx }}
    >
      <Box sx={{ ...availabilityModalStyles.modalStyles, ...customStyle }}>
        <Box sx={availabilityModalStyles.directLinkHeader}>
          {/* Cross */}
          <img
            src={CrossIcon}
            style={availabilityModalStyles.closeIcon}
            onClick={close}
          />
        </Box>

        {/* Body */}
        <Box sx={availabilityModalStyles.directLinkBodyContainer}>
          <Typography sx={availabilityModalStyles.customerName}>
            Share with {formatCustomerName(customerDetails?.customer_name)}?
          </Typography>

          <Box sx={availabilityModalStyles.directLinkModalBody}>
            <img
              src={getItemImageSource(item)}
              style={availabilityModalStyles.imageStyles as React.CSSProperties}
            />

            <Box padding={"8px"}>
              <Typography sx={availabilityModalStyles.cardTitle}>
                {itemName}
              </Typography>
              <hr />
              <Typography sx={availabilityModalStyles.cardDate}>
                {startDate.tz("America/New_York").format("ddd, MMM DD, yyyy")}
              </Typography>

              <Typography sx={availabilityModalStyles.cardTime}>
                {startTime.local().format("hh:mm A")} -{" "}
                {endTime.local().format("hh:mm A")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                <Typography sx={availabilityModalStyles.cardPrice}>
                  Price:{" "}
                  <Typography style={availabilityModalStyles.boldText}>
                    ${price}
                  </Typography>{" "}
                </Typography>
                <Typography sx={availabilityModalStyles.cardPays}>
                  Pays:{" "}
                  <Typography style={availabilityModalStyles.boldText}>
                    {commission}% (${(price - discountedPrice).toFixed(0)})
                  </Typography>
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Footer */}
          <Box sx={availabilityModalStyles.directLinkModalFooter}>
            <Button
              variant="contained"
              sx={availabilityModalStyles.flex1}
              onClick={handleShareClick}
              disabled={loading}
            >
              Share
            </Button>
            <Button
              variant="outlined"
              sx={availabilityModalStyles.flex1}
              onClick={close}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ShareDirectLinkModal;
